<script setup>
import {onMounted, ref} from 'vue';
import {authData, getLoggedUser} from '@/services/AuthService';
import clientService from '@/services/ClientService';
import {useI18n} from 'vue-i18n';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import PaymentQrCodeModal from '@/components/modals/PaymentQrCodeModal.vue';
import {formatAmount} from '@/helper';

const {t} = useI18n()
const loggedClient = ref(null);
const loggedUser = ref(null);
const paymentTerms = ref(null);
const loading = ref(true);

function openQrCodeModal(item) {
  const {reveal} = createConfirmDialog(PaymentQrCodeModal, {
    qrCodeBase64: item.payment_qr_code_base64,
    bankAccountNumber: item.bank_account_number_full,
    variableSymbol: item.variable_symbol,
  });
  reveal();
}

onMounted(async () => {
  getLoggedUser().then(response => {
    loggedClient.value = response.client;
    loggedUser.value = response;
    getPaymentTerms();
  });
})

/**
 * Nákupy dluhopisů (type BOND) jsou na konci položek. Nadpis Dluhopisy se ukáže nad prvním z nich, jen když má klient
 * i strategie (stejně jako v e-mailu Platební údaje).
 */
function showBondsHeading(index) {
  const items = paymentTerms.value?.items ?? [];
  const isBond = item => item?.type === 'BOND';
  return isBond(items[index]) && !isBond(items[index - 1]) && items.some(item => !isBond(item));
}

/** Štítek stavu nákupu dluhopisu – stejné barvy jako v Pokynech na nástěnce Dluhopisy. */
const bondStateBadge = {AWAITING_ESO: 'bg-primary', AWAITING_PAYMENT: 'bg-warning text-dark'};

function getPaymentTerms() {
  clientService.getPaymentTerms(loggedClient.value.id).then(response => {
    paymentTerms.value = response;
    loading.value = false;
  });
}

</script>
<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="page-section__title">
        {{t('paymentTerms.title')}}
      </div>
    </div>
    <div class="page-section__content">
      <div v-if="loading" class="text-center">
        <div class="row border-bottom"  v-for="i in 5" :key="i">
          <div class="col-12">
            <div class="row p-3">
              <div class="col-12">
                <Skeletor width="100%" height="100" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!loading">
        <template v-for="(item, index) of paymentTerms?.items" :key="index">
        <div v-if="showBondsHeading(index)" class="page-section__title mt-5">{{ t('onboardingBonds.title') }}</div>
        <div class="row border-bottom">
          <div class="col-12">
            <div class="row p-3 justify-content-center align-items-center mb-4">
              <div class="col-12 col-md-6">
                <div class="mb-2">
                  <strong class="me-2">{{item.strategy_name}}</strong>
                  <!-- nákup dluhopisu: stav nákupu (zpracovává se / čeká na platbu) místo aktivní/neaktivní strategie -->
                  <span v-if="item.type === 'BOND'" class="badge" :class="bondStateBadge[item.state]">{{ t('bondsDetail.client_bond_states.' + item.state) }}</span>
                  <template v-else>
                    <span v-if="item.is_active" class="badge bg-primary">{{t('paymentTerms.active')}}</span>
                    <span v-if="!item.is_active" class="badge bg-secondary">{{t('paymentTerms.inactive')}}</span>
                  </template>
                </div>
                <div v-if="item.contract">{{t('paymentTerms.contract')}}: <strong>{{item.contract.contract_type?.name}} ({{ item.contract.contractNumber }})</strong></div>
                <div>{{t('paymentTerms.bank_account_number')}}: <strong>{{item.bank_account_number_full}}</strong></div>
                <div>{{t('paymentTerms.variable_symbol')}}: <strong>{{item.variable_symbol}}</strong></div>
                <!-- nákup dluhopisu se platí jednorázově – částka k úhradě vč. AÚV (je i v QR kódu) -->
                <div v-if="item.type === 'BOND'">{{ t('bondPurchase.payment_amount') }}: <strong>{{ formatAmount(item.amount, item.currency) }}</strong></div>
              </div>
              <div class="col-12 col-md-6 mt-3 mt-md-0 text-md-end">
                <button type="button" class="btn btn-primary" @click="openQrCodeModal(item)">{{t('paymentTerms.show_qr_code')}}</button>
              </div>
              <div v-if="!item.is_active" class="col-12 text-muted">
                <small>{{t('paymentTerms.payment_description')}}</small>
              </div>
            </div>
          </div>
        </div>
        </template>
        <div class="page-section__title mt-5">
          {{t('paymentTerms.payment_in_other_currencies')}}
        </div>
        <div v-for="item of paymentTerms?.other_accounts" class="row border-bottom">
          <div class="col-12">
            <div class="row p-3 justify-content-center align-items-center">
              <div class="col-12">
                <div class="mb-2"><strong>{{item.currency}}</strong></div>
                <div>IBAN: <strong>{{item.iban}}</strong></div>
                <div>BIC/SWIFT: <strong>{{item.bicSwift}}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

